<template>
  <div v-if="loading" class="table-group">
    <div class="table-loader"></div>
  </div>

  <div v-else-if="loadError" class="inner-box">
    <div class="mc-error">{{ loadError }}</div>
    <div class="inner-box">
      <router-link class="btn-outline" :to="orderRoute">{{ t('modals.cancel') }}</router-link>
    </div>
  </div>

  <template v-else-if="canSendMessage">
    <div class="header-wrapper txt-center">
      <div class="h1">{{ t('orderMessages.sendTitle') }}</div>
      <p class="font-16">{{ t('orderPage.title', {number: order.number}) }}</p>
    </div>

    <div class="inner-box">
      <div class="form-item">
        <label class="form-label" for="order-message-text">{{ t('orderMessages.messageText') }}</label>
        <textarea
          id="order-message-text"
          v-model="text"
          class="textarea order-message-textarea"
          rows="8"
          :maxlength="maxLength + 1"
          :placeholder="t('orderMessages.messagePlaceholder')"
          :disabled="submitting"
        ></textarea>
      </div>
      <div class="muted-13 txt-right">{{ text.length }} / {{ maxLength }}</div>
      <div v-if="error" class="mc-error">{{ error }}</div>
      <div class="flex-center">
        <button class="btn" type="button" :class="{loader: submitting}" :disabled="!canSubmit" @click="send">
          {{ t('orderMessages.send') }}
        </button>
        <router-link class="btn-outline" :to="orderRoute" :aria-disabled="submitting" @click="preventCancelWhileSubmitting">
          {{ t('modals.cancel') }}
        </router-link>
      </div>
    </div>
  </template>
</template>

<script>
import axios from 'axios';
import {mapActions} from 'vuex';
import {useI18n} from 'vue-i18n';
import {translateApiError, translateApiErrors} from '@/utils/apiErrorUtils';

export default {
  data: () => ({
    order: {},
    text: '',
    loading: true,
    submitting: false,
    submitted: false,
    error: '',
    loadError: '',
    maxLength: 2000,
  }),
  setup() {
    const {t} = useI18n();
    return {t};
  },
  created() {
    this.loadOrder();
  },
  watch: {
    '$route.params.id'() {
      this.loadOrder();
    },
  },
  computed: {
    orderId() {
      return Number(this.$route.params.id);
    },
    orderRoute() {
      return Number.isInteger(this.orderId) && this.orderId > 0
        ? {name: 'order', params: {id: this.orderId}, query: this.$route.query}
        : {name: 'main'};
    },
    canSendMessage() {
      return Boolean(this.order?.permissions?.can_send_message);
    },
    canSubmit() {
      return this.canSendMessage && !this.submitting && !this.submitted
        && this.text.trim().length > 0 && this.text.length <= this.maxLength;
    },
  },
  methods: {
    ...mapActions('notify', ['addNotify']),
    async loadOrder() {
      this.loading = true;
      this.loadError = '';
      this.order = {};

      if (!Number.isInteger(this.orderId) || this.orderId <= 0) {
        this.loadError = this.t('apiErrors.notFound');
        this.loading = false;
        return;
      }

      try {
        const response = await axios.get(`/api/order/${this.orderId}/`);
        if (response.data.status === 'error') {
          this.loadError = translateApiErrors(response.data.errors, this.t).join(' ');
          return;
        }

        this.order = response.data.data?.order || {};
        if (!this.order.id) {
          this.loadError = this.t('apiErrors.notFound');
        } else if (!this.canSendMessage) {
          this.loadError = this.t('apiErrors.access');
        }
      } catch (error) {
        const apiErrors = error.response?.data?.errors;
        this.loadError = apiErrors
          ? translateApiErrors(apiErrors, this.t).join(' ')
          : (error.message || this.t('errors.serverError'));
      } finally {
        this.loading = false;
      }
    },
    preventCancelWhileSubmitting(event) {
      if (this.submitting) event.preventDefault();
    },
    async send() {
      if (!this.canSubmit) return;
      this.submitting = true;
      this.error = '';
      try {
        const formData = new FormData();
        formData.append('text', this.text);
        const response = await axios.post(`/api/order/${this.orderId}/message/`, formData);
        if (response.data.status === 'error') {
          throw new Error(translateApiError(response.data.errors?.[0], this.t));
        }
        const result = response.data.data;
        this.submitted = true;
        this.addNotify({
          text: this.t(result.email_sent ? 'orderMessages.sent' : 'orderMessages.savedWithoutEmail'),
          error: !result.email_sent,
        });
        await this.$router.push(this.orderRoute);
      } catch (error) {
        const apiError = error.response?.data?.errors?.[0];
        this.error = apiError
            ? translateApiError(apiError, this.t)
            : (error.message || this.t('orderMessages.sendError'));
      } finally {
        if (!this.submitted) this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.order-message-textarea { min-height: 180px; resize: vertical; }
.form-label { display: block; margin-bottom: 8px; }
.txt-right { text-align: right; margin-top: 6px; }
</style>
